"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale } from "@/lib/labels/LabelsProvider";
export interface SearchEntity {
  entityId: string;
  name: string;
  entityType?: string;
  sport?: string;
  subtitle?: string;
  imageUrl?: string;
  position?: string;
}
export interface SearchArticle {
  id: number;
  title: string;
  slug: string;
  heroImage: {
    url: string;
    alt: string | null;
  } | null;
  categories: {
    id: number;
    title: string;
  }[];
  publishedAt: string | null;
  excerpt: string | null;
}
interface EntitiesResponse {
  results?: SearchEntity[];
}
interface ArticlesResponse {
  results?: SearchArticle[];
  totalDocs?: number;
  totalPages?: number;
  page?: number;
  hasNextPage?: boolean;
}
interface UseSearchPageResult {
  query: string;
  setQuery: (q: string) => void;
  teams: SearchEntity[];
  players: SearchEntity[];
  competitions: SearchEntity[];
  entitiesTotal: number;
  entitiesLoading: boolean;
  articles: SearchArticle[];
  articlesTotal: number;
  articlesLoading: boolean;
  articlesLoadingMore: boolean;
  hasNextArticlesPage: boolean;
  articlesError: boolean;
  loadMoreArticles: () => void;
  retryArticles: () => void;
}
const ARTICLES_PAGE_SIZE = 10;
const ENTITY_CAP = 6;
const DEBOUNCE_MS = 300;
export function useSearchPage(skipEntitySearch = false): UseSearchPageResult {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQueryState] = useState(initialQuery);
  const [teams, setTeams] = useState<SearchEntity[]>([]);
  const [players, setPlayers] = useState<SearchEntity[]>([]);
  const [competitions, setCompetitions] = useState<SearchEntity[]>([]);
  const [entitiesTotal, setEntitiesTotal] = useState(0);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [articles, setArticles] = useState<SearchArticle[]>([]);
  const [articlesPage, setArticlesPage] = useState(1);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [hasNextArticlesPage, setHasNextArticlesPage] = useState(false);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesLoadingMore, setArticlesLoadingMore] = useState(false);
  const [articlesError, setArticlesError] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    const params = new URLSearchParams(searchParams.toString());
    if (q) {
      params.set("q", q);
    } else {
      params.delete("q");
    }
    const qs = params.toString();
    router.replace(qs ? `?${qs}` : "?", {
      scroll: false
    });
  }, [router, searchParams]);
  const runSearch = useCallback(async (q_0: string, page: number, append: boolean) => {
    const requestId = ++requestIdRef.current;
    if (append) {
      setArticlesLoadingMore(true);
    } else {
      setEntitiesLoading(true);
      setArticlesLoading(true);
      setArticlesError(false);
    }
    const entitiesPromise = append || skipEntitySearch ? Promise.resolve<EntitiesResponse | null>(null) : fetch(`/api/entities/search?q=${encodeURIComponent(q_0)}&type=team,athlete,competition&locale=${locale}`).then(r => r.json() as Promise<EntitiesResponse>).catch(() => null);
    const articlesPromise = fetch(`/api/posts/search?q=${encodeURIComponent(q_0)}&locale=${locale}&page=${page}&limit=${ARTICLES_PAGE_SIZE}`).then(r_0 => r_0.json() as Promise<ArticlesResponse>).catch(() => null);
    const [entitiesResult, articlesResult] = await Promise.all([entitiesPromise, articlesPromise]);
    if (requestId !== requestIdRef.current) return;
    if (!append) {
      // We only render football entities — other sports have no public page yet
      const allEntities = (entitiesResult?.results ?? []).filter(e => e.entityId.startsWith("fb:"));
      setTeams(allEntities.filter(e_0 => e_0.entityType === "team").slice(0, ENTITY_CAP));
      setPlayers(allEntities.filter(e_1 => e_1.entityType === "athlete").slice(0, ENTITY_CAP));
      setCompetitions(allEntities.filter(e_2 => e_2.entityType === "competition").slice(0, ENTITY_CAP));
      setEntitiesTotal(allEntities.length);
      setEntitiesLoading(false);
    }
    if (articlesResult && Array.isArray(articlesResult.results)) {
      setArticles(prev => append ? [...prev, ...articlesResult.results!] : articlesResult.results!);
      setArticlesTotal(articlesResult.totalDocs ?? articlesResult.results.length);
      setHasNextArticlesPage(Boolean(articlesResult.hasNextPage));
      setArticlesError(false);
    } else if (!append) {
      setArticles([]);
      setArticlesTotal(0);
      setHasNextArticlesPage(false);
      setArticlesError(true);
    } else {
      setArticlesError(true);
    }
    if (append) {
      setArticlesLoadingMore(false);
    } else {
      setArticlesLoading(false);
    }
  }, [locale]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      requestIdRef.current++;
      setTeams([]);
      setPlayers([]);
      setCompetitions([]);
      setEntitiesTotal(0);
      setArticles([]);
      setArticlesTotal(0);
      setHasNextArticlesPage(false);
      setArticlesPage(1);
      setEntitiesLoading(false);
      setArticlesLoading(false);
      setArticlesError(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setArticlesPage(1);
      runSearch(query.trim(), 1, false);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);
  const loadMoreArticles = useCallback(() => {
    if (articlesLoadingMore || !hasNextArticlesPage) return;
    const nextPage = articlesPage + 1;
    setArticlesPage(nextPage);
    runSearch(query.trim(), nextPage, true);
  }, [articlesLoadingMore, hasNextArticlesPage, articlesPage, query, runSearch]);
  const retryArticles = useCallback(() => {
    if (query.trim().length < 2) return;
    runSearch(query.trim(), articlesPage, articlesPage > 1);
  }, [query, articlesPage, runSearch]);
  return {
    query,
    setQuery,
    teams,
    players,
    competitions,
    entitiesTotal,
    entitiesLoading,
    articles,
    articlesTotal,
    articlesLoading,
    articlesLoadingMore,
    hasNextArticlesPage,
    articlesError,
    loadMoreArticles,
    retryArticles
  };
}